<template>
    <AppLayout>
        <div class="bg-white shadow overflow-hidden sm:rounded-lg mb-5">
            <div class="px-4 py-5 sm:px-6">
                <h2 class="text-lg leading-6 font-medium text-gray-900">
                    {{ $t("projects.showTitle", { project: project.data.name }) }}
                </h2>
                <p class="mt-1 max-w-2xl text-sm text-gray-500">
                    {{ $t("projects.description", { tenant: project.data.tenant.name }) }}
                </p>
            </div>
            <div class="border-t border-gray-200 px-4 py-5 sm:p-0">
                <dl class="sm:divide-y sm:divide-gray-200">
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">{{ $t("projects.forms.name.title") }}</dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ project.data.name }}</dd>
                    </div>
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">{{ $t("projects.forms.website_url.title") }}</dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <a
                                :href="project.data.website_url"
                                target="_blank"
                                class="font-medium text-lightblue-700 hover:text-lightblue-500"
                            >
                                {{ project.data.website_url }}
                            </a>
                        </dd>
                    </div>
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">{{ $t("projects.forms.type.title") }}</dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {{ project.data.type }}
                        </dd>
                    </div>
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">{{ $t("projects.forms.description.title") }}</dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {{ project.data.description }}
                        </dd>
                    </div>

                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">
                            {{ $t("projects.forms.active_incident.title") }}
                        </dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <div class="flex">
                                <div
                                    v-if="project.data.active_incident"
                                    class="bg-yellow-400 text-yellow-700 rounded-full px-4 py-1 text-sm"
                                >
                                    {{ $t("tenants.table.status_items.active_incident") }}
                                </div>

                                <div v-else class="bg-green-300 text-white rounded-full px-4 py-1 text-sm">
                                    {{ $t("tenants.table.status_items.working_fine") }}
                                </div>
                            </div>
                        </dd>
                    </div>
                </dl>
            </div>
        </div>

        <div class="bg-white shadow overflow-hidden sm:rounded-lg mb-5">
            <div class="px-4 py-5 sm:px-6">
                <h2 class="text-lg leading-6 font-medium text-gray-900">
                    {{ $t("tenants.edit.title", { name: project.data.tenant.name }) }}
                </h2>
                <p class="mt-1 max-w-2xl text-sm text-gray-500">{{ $t("tenants.edit.description") }}</p>
            </div>
            <div class="border-t border-gray-200 px-4 py-5 sm:p-0">
                <dl class="sm:divide-y sm:divide-gray-200">
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">{{ $t("tenants.forms.id.title") }}</dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ project.data.tenant.id }}</dd>
                    </div>
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">{{ $t("tenants.forms.url.title") }}</dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <Link
                                :href="`${env.url}/${project.data.tenant.id}`"
                                class="font-medium text-lightblue-700 hover:text-lightblue-500"
                            >
                                {{ `${env.url}/${project.data.tenant.id}` }}
                            </Link>
                        </dd>
                    </div>
                    <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt class="text-sm font-medium text-gray-500">
                            {{ $t("airtable.forms.airtable_record.title") }}
                        </dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <a
                                :href="`https://airtable.com/tbluw0XnSaCR0JA1Z/viwKaFmE2tSl29lb3/${project.data.tenant.airtable_record_id}`"
                                target="_blank"
                                class="font-medium text-lightblue-700 hover:text-lightblue-500"
                            >
                                {{ project.data.tenant.airtable_record_id }}
                            </a>
                        </dd>
                    </div>
                </dl>
            </div>
        </div>

        <div class="overflow-hidden sm:rounded-lg">
            <div class="pr-4 py-5 sm:pr-6">
                <h2 class="text-lg font-medium text-gray-900">{{ $t("projects.descriptionOtherProjects") }}</h2>
            </div>

            <div v-if="!hasProjects" class="max-w-7xl mx-auto">
                <div class="w-full m-auto justify-center items-center">
                    <div class="flex">
                        <fa-icon icon="smile-wink" class="mx-auto text-7xl text-gray-300"></fa-icon>
                    </div>
                    <h2 class="my-2 text-4xl text-gray-300 text-center">
                        {{ $t("projects.no_projects_found") }}
                    </h2>
                </div>
            </div>

            <div v-if="hasProjects" class="px-4 py-5 sm:p-0">
                <Table :headers="['name', 'type', 'active_incident']" :content="currentProjects" :actions="actions" />
            </div>
        </div>
    </AppLayout>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import Table from "@/Components/Table";
import env from "@/env";
import { computed } from "vue";
import { Link } from "@inertiajs/inertia-vue3";

export default {
    name: "ShowProject",
    components: { AppLayout, Table, Link },
    props: {
        project: Object,
        otherProjects: Object,
    },
    setup(props) {
        const currentProjects = computed(() => (props.otherProjects.data ? props.otherProjects.data : []));
        const hasProjects = computed(() => props.otherProjects.data.length > 0);
        const actions = [
            {
                name: "Show",
                route: "universal.home",
            },
        ];

        return {
            env,
            hasProjects,
            currentProjects,
            actions,
        };
    },
};
</script>
